import { jsPDF } from "jspdf";

const PAGE_W = 210;
const PAGE_H = 297;
const M = 15;
const CONTENT_W = PAGE_W - M * 2;
const BOTTOM = 15;

const THEMES = {
  minimal: {
    accent: [39, 39, 42],
    ink: [24, 24, 27],
    muted: [82, 82, 91],
    pale: [244, 244, 245],
    font: "helvetica",
    header: "minimal",
    section: "rule",
    skill: "text",
    order: [
      "summary",
      "experience",
      "education",
      "skills",
      "certifications",
      "projects",
      "languages",
    ],
  },

  modern: {
    accent: [37, 99, 235],
    ink: [15, 23, 42],
    muted: [71, 85, 105],
    pale: [239, 246, 255],
    font: "helvetica",
    header: "modern",
    section: "bar",
    skill: "pill",
    order: [
      "summary",
      "experience",
      "skills",
      "education",
      "projects",
      "certifications",
      "languages",
    ],
  },

  engineering: {
    accent: [8, 145, 178],
    ink: [15, 23, 42],
    muted: [71, 85, 105],
    pale: [236, 254, 255],
    font: "helvetica",
    header: "technical",
    section: "technical",
    skill: "technical",
    order: [
      "summary",
      "skills",
      "experience",
      "projects",
      "education",
      "certifications",
      "languages",
    ],
  },

  executive: {
    accent: [30, 41, 59],
    ink: [15, 23, 42],
    muted: [100, 116, 139],
    pale: [241, 245, 249],
    font: "times",
    header: "executive",
    section: "classic",
    skill: "text",
    order: [
      "summary",
      "experience",
      "education",
      "skills",
      "certifications",
      "languages",
      "projects",
    ],
  },

  graduate: {
    accent: [5, 150, 105],
    ink: [15, 23, 42],
    muted: [71, 85, 105],
    pale: [236, 253, 245],
    font: "helvetica",
    header: "graduate",
    section: "graduate",
    skill: "pill",
    order: [
      "summary",
      "education",
      "projects",
      "skills",
      "experience",
      "certifications",
      "languages",
    ],
  },

  swiss: {
    accent: [220, 38, 38],
    ink: [17, 24, 39],
    muted: [75, 85, 99],
    pale: [254, 242, 242],
    font: "helvetica",
    header: "swiss",
    section: "numbered",
    skill: "text",
    order: [
      "summary",
      "experience",
      "skills",
      "education",
      "projects",
      "certifications",
      "languages",
    ],
  },

  consulting: {
    accent: [109, 40, 217],
    ink: [24, 24, 27],
    muted: [82, 82, 91],
    pale: [245, 243, 255],
    font: "helvetica",
    header: "consulting",
    section: "numberedRule",
    skill: "text",
    order: [
      "summary",
      "experience",
      "education",
      "skills",
      "projects",
      "certifications",
      "languages",
    ],
  },

  elegant: {
    accent: [124, 90, 54],
    ink: [41, 37, 36],
    muted: [87, 83, 78],
    pale: [247, 244, 240],
    font: "times",
    header: "elegant",
    section: "ornate",
    skill: "text",
    order: [
      "summary",
      "experience",
      "education",
      "skills",
      "certifications",
      "projects",
      "languages",
    ],
  },

  tech: {
    accent: [15, 118, 110],
    ink: [19, 78, 74],
    muted: [71, 85, 105],
    pale: [240, 253, 250],
    font: "helvetica",
    header: "tech",
    section: "tech",
    skill: "code",
    order: [
      "summary",
      "skills",
      "experience",
      "projects",
      "education",
      "certifications",
      "languages",
    ],
  },

  creative: {
    accent: [219, 39, 119],
    ink: [31, 41, 55],
    muted: [75, 85, 99],
    pale: [253, 242, 248],
    font: "helvetica",
    header: "creative",
    section: "creative",
    skill: "pill",
    order: [
      "summary",
      "experience",
      "skills",
      "education",
      "projects",
      "certifications",
      "languages",
    ],
  },
};

const clean = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const has = (value) =>
  clean(value).length > 0;

const arr = (value) =>
  Array.isArray(value) ? value : [];

const safeFileName = (name) =>
  (clean(name) || "resume")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "_")
    .slice(0, 80) || "resume";

const dateRange = (item) => {
  const start = clean(item?.start);

  const end = item?.current
    ? "Present"
    : clean(item?.end);

  return [start, end]
    .filter(Boolean)
    .join(" – ");
};

const getContacts = (data) =>
  [
    data.email,
    data.phone,
    data.location,
    data.linkedin,
  ]
    .map(clean)
    .filter(Boolean);

export function downloadResumePDF(resume) {
  const data = resume?.data || {};

  const theme =
    THEMES[resume?.template] ||
    THEMES.minimal;

  const doc = new jsPDF({
    unit: "mm",
    format: "a4",
    compress: true,
    putOnlyUsedFonts: true,
  });

  let y = M;

  const setFont = (
    size,
    style = "normal",
    color = theme.ink
  ) => {
    doc.setFont(
      theme.font,
      style
    );

    doc.setFontSize(size);

    doc.setTextColor(
      ...color
    );
  };

  const drawLine = (
    x1,
    y1,
    x2,
    y2,
    width = 0.35,
    color = theme.accent
  ) => {
    doc.setDrawColor(
      ...color
    );

    doc.setLineWidth(width);

    doc.line(
      x1,
      y1,
      x2,
      y2
    );
  };

  const drawFill = (
    x,
    top,
    width,
    height,
    color,
    radius = 0
  ) => {
    doc.setFillColor(
      ...color
    );

    if (radius > 0) {
      doc.roundedRect(
        x,
        top,
        width,
        height,
        radius,
        radius,
        "F"
      );
    } else {
      doc.rect(
        x,
        top,
        width,
        height,
        "F"
      );
    }
  };

  const newPage = () => {
    doc.addPage();

    y = M;
  };

  const ensure = (
    height = 6
  ) => {
    if (
      y + height <=
      PAGE_H - BOTTOM
    ) {
      return false;
    }

    newPage();

    return true;
  };

  const wrapped = (
    text,
    width
  ) =>
    doc.splitTextToSize(
      clean(text),
      Math.max(
        10,
        width
      )
    );

  const textBlock = (
    text,
    x,
    width,
    size = 9,
    leading = 4,
    style = "normal",
    color = theme.ink
  ) => {
    if (!has(text)) {
      return 0;
    }

    setFont(
      size,
      style,
      color
    );

    const lines =
      wrapped(
        text,
        width
      );

    lines.forEach(
      (lineText) => {
        ensure(leading);

        doc.text(
          lineText,
          x,
          y
        );

        y += leading;
      }
    );

    return (
      lines.length *
      leading
    );
  };

  const rightText = (
    text,
    rightX,
    baseline,
    size = 7.8,
    color = theme.muted
  ) => {
    if (!has(text)) {
      return;
    }

    setFont(
      size,
      "normal",
      color
    );

    doc.text(
      clean(text),
      rightX,
      baseline,
      {
        align: "right",
      }
    );
  };

  /*
   * Draw a title on the left and date on the right.
   *
   * IMPORTANT:
   * The title width is calculated from the actual date width.
   * This prevents long job titles from touching or crossing dates.
   */
  const drawTitleRow = (
    title,
    date,
    left = M,
    right = PAGE_W - M,
    size = 9.4
  ) => {
    const titleText =
      clean(title);

    const dateText =
      clean(date);

    setFont(
      size,
      "bold",
      theme.ink
    );

    const dateWidth =
      dateText
        ? doc.getTextWidth(
            dateText
          )
        : 0;

    const gap =
      dateText
        ? 7
        : 0;

    const availableTitleWidth =
      Math.max(
        25,
        right -
          left -
          dateWidth -
          gap
      );

    const lines =
      wrapped(
        titleText,
        availableTitleWidth
      );

    lines.forEach(
      (lineText, index) => {
        ensure(
          size >= 9
            ? 6
            : 5
        );

        doc.text(
          lineText,
          left,
          y
        );

        if (
          index === 0 &&
          dateText
        ) {
          rightText(
            dateText,
            right,
            y,
            7.8,
            theme.muted
          );
        }

        y +=
          size >= 9.5
            ? 4.3
            : 4;
      }
    );

    return lines.length;
  };

  /*
   * Section headings are also measured dynamically.
   * No fixed "line starts at 48mm" positions.
   */
  const sectionTitle = (
    title,
    index
  ) => {
    const label =
      clean(title).toUpperCase();

    ensure(14);

    if (
      theme.section ===
      "rule"
    ) {
      y += 2;

      setFont(
        9.1,
        "bold",
        theme.ink
      );

      doc.text(
        label,
        M,
        y
      );

      const textEnd =
        M +
        doc.getTextWidth(
          label
        );

      const lineStart =
        Math.min(
          PAGE_W - M - 10,
          textEnd + 7
        );

      drawLine(
        lineStart,
        y - 1.1,
        PAGE_W - M,
        y - 1.1,
        0.3,
        [160, 160, 165]
      );

      y += 6;

      return;
    }

    if (
      theme.section ===
      "bar"
    ) {
      y += 2;

      drawFill(
        M,
        y - 4,
        2.2,
        5.7,
        theme.accent,
        0.8
      );

      setFont(
        9.2,
        "bold",
        theme.ink
      );

      doc.text(
        label,
        M + 5,
        y
      );

      const textEnd =
        M +
        5 +
        doc.getTextWidth(
          label
        );

      const lineStart =
        Math.min(
          PAGE_W - M - 10,
          textEnd + 6
        );

      drawLine(
        lineStart,
        y - 1.1,
        PAGE_W - M,
        y - 1.1,
        0.35,
        theme.pale
      );

      y += 6;

      return;
    }

    if (
      theme.section ===
      "technical"
    ) {
      y += 2;

      drawFill(
        M,
        y - 4,
        1.7,
        5.8,
        theme.accent
      );

      setFont(
        9.2,
        "bold",
        theme.accent
      );

      doc.text(
        label,
        M + 5,
        y
      );

      drawLine(
        M + 5,
        y + 2,
        PAGE_W - M,
        y + 2,
        0.55,
        theme.accent
      );

      y += 7;

      return;
    }

    if (
      theme.section ===
      "classic"
    ) {
      y += 2;

      setFont(
        10,
        "bold",
        theme.accent
      );

      doc.text(
        label,
        M,
        y
      );

      drawLine(
        M,
        y + 2.3,
        PAGE_W - M,
        y + 2.3,
        0.3,
        theme.accent
      );

      y += 7.5;

      return;
    }

    if (
      theme.section ===
      "graduate"
    ) {
      y += 2;

      setFont(
        9.8,
        "bold",
        theme.accent
      );

      doc.text(
        clean(title),
        M,
        y
      );

      const textWidth =
        doc.getTextWidth(
          clean(title)
        );

      drawLine(
        M,
        y + 2.4,
        Math.min(
          PAGE_W - M,
          M +
            textWidth +
            7
        ),
        y + 2.4,
        1,
        theme.accent
      );

      y += 7;

      return;
    }

    if (
      theme.section ===
      "numbered"
    ) {
      y += 2;

      setFont(
        7.5,
        "bold",
        theme.accent
      );

      doc.text(
        String(index).padStart(
          2,
          "0"
        ),
        M,
        y
      );

      setFont(
        9.7,
        "bold",
        theme.ink
      );

      doc.text(
        label,
        M + 8,
        y
      );

      const textEnd =
        M +
        8 +
        doc.getTextWidth(
          label
        );

      const lineStart =
        Math.min(
          PAGE_W - M - 10,
          textEnd + 6
        );

      drawLine(
        lineStart,
        y - 1.2,
        PAGE_W - M,
        y - 1.2,
        0.45,
        theme.accent
      );

      y += 6.5;

      return;
    }

    if (
      theme.section ===
      "numberedRule"
    ) {
      y += 2;

      setFont(
        7.5,
        "bold",
        theme.accent
      );

      doc.text(
        String(index).padStart(
          2,
          "0"
        ),
        M,
        y
      );

      setFont(
        9.7,
        "bold",
        theme.ink
      );

      doc.text(
        label,
        M + 7,
        y
      );

      y += 3.5;

      drawLine(
        M,
        y,
        PAGE_W - M,
        y,
        0.75,
        theme.accent
      );

      y += 4.5;

      return;
    }

    if (
      theme.section ===
      "ornate"
    ) {
      y += 2;

      setFont(
        9.8,
        "bold",
        theme.accent
      );

      doc.text(
        label,
        M,
        y
      );

      const textEnd =
        M +
        doc.getTextWidth(
          label
        );

      drawLine(
        textEnd + 7,
        y - 1.2,
        PAGE_W - M,
        y - 1.2,
        0.35,
        theme.accent
      );

      y += 7;

      return;
    }

    if (
      theme.section ===
      "tech"
    ) {
      y += 2;

      setFont(
        9.2,
        "bold",
        theme.accent
      );

      doc.text(
        label,
        M + 4,
        y
      );

      drawFill(
        M,
        y - 4,
        1.5,
        5.7,
        theme.accent
      );

      drawLine(
        M + 4,
        y + 2,
        PAGE_W - M,
        y + 2,
        0.5,
        theme.accent
      );

      y += 7;

      return;
    }

    // Creative
    y += 2;

    drawFill(
      M,
      y - 4.5,
      3.2,
      3.2,
      theme.accent,
      1.6
    );

    setFont(
      9.5,
      "bold",
      theme.accent
    );

    doc.text(
      label,
      M + 6,
      y
    );

    const textEnd =
      M +
      6 +
      doc.getTextWidth(
        label
      );

    const lineStart =
      Math.min(
        PAGE_W - M - 10,
        textEnd + 6
      );

    drawLine(
      lineStart,
      y - 1.1,
      PAGE_W - M,
      y - 1.1,
      0.35,
      theme.accent
    );

    y += 6.5;
  };

  const drawHeader = () => {
    const name =
      clean(data.full_name) ||
      "Your Name";

    const role =
      clean(data.target_role);

    const contacts =
      getContacts(data);

    if (
      theme.header ===
        "executive" ||
      theme.header ===
        "elegant"
    ) {
      setFont(
        21,
        "bold",
        theme.ink
      );

      doc.text(
        name.toUpperCase(),
        PAGE_W / 2,
        y,
        {
          align: "center",
        }
      );

      y += 7;

      if (role) {
        setFont(
          9,
          "normal",
          theme.accent
        );

        doc.text(
          role.toUpperCase(),
          PAGE_W / 2,
          y,
          {
            align: "center",
          }
        );

        y += 5;
      }

      if (
        contacts.length
      ) {
        setFont(
          7.8,
          "normal",
          theme.muted
        );

        const contactText =
          contacts.join(
            "  •  "
          );

        const lines =
          wrapped(
            contactText,
            CONTENT_W
          );

        lines.forEach(
          (lineText) => {
            doc.text(
              lineText,
              PAGE_W / 2,
              y,
              {
                align: "center",
              }
            );

            y += 3.6;
          }
        );
      }

      drawLine(
        M,
        y + 1,
        PAGE_W - M,
        y + 1,
        0.8,
        theme.accent
      );

      y += 7;

      return;
    }

    if (
      theme.header ===
      "swiss"
    ) {
      setFont(
        23,
        "bold",
        theme.ink
      );

      doc.text(
        name,
        M,
        y
      );

      y += 8;

      if (role) {
        setFont(
          8.8,
          "bold",
          theme.accent
        );

        doc.text(
          role.toUpperCase(),
          M,
          y
        );

        y += 5;
      }

      if (
        contacts.length
      ) {
        setFont(
          7.7,
          "normal",
          theme.muted
        );

        const contactText =
          contacts.join(
            "  /  "
          );

        wrapped(
          contactText,
          CONTENT_W
        ).forEach(
          (lineText) => {
            doc.text(
              lineText,
              M,
              y
            );

            y += 3.5;
          }
        );
      }

      drawLine(
        M,
        y + 1,
        PAGE_W - M,
        y + 1,
        1.05,
        theme.accent
      );

      y += 7;

      return;
    }

    if (
      theme.header ===
      "consulting"
    ) {
      setFont(
        20,
        "bold",
        theme.ink
      );

      doc.text(
        name,
        M,
        y
      );

      if (role) {
        setFont(
          8.7,
          "bold",
          theme.accent
        );

        const roleWidth =
          doc.getTextWidth(
            role
          );

        const roleX =
          Math.max(
            M,
            PAGE_W -
              M -
              roleWidth
          );

        doc.text(
          role,
          roleX,
          y
        );
      }

      y += 7;

      if (
        contacts.length
      ) {
        setFont(
          7.8,
          "normal",
          theme.muted
        );

        wrapped(
          contacts.join(
            "  |  "
          ),
          CONTENT_W
        ).forEach(
          (lineText) => {
            doc.text(
              lineText,
              M,
              y
            );

            y += 3.5;
          }
        );
      }

      drawLine(
        M,
        y + 1,
        PAGE_W - M,
        y + 1,
        0.7,
        theme.accent
      );

      y += 7;

      return;
    }

    if (
      theme.header ===
      "graduate"
    ) {
      setFont(
        23,
        "bold",
        theme.ink
      );

      doc.text(
        name,
        M,
        y
      );

      y += 8;

      if (role) {
        setFont(
          9.2,
          "bold",
          theme.accent
        );

        doc.text(
          role,
          M,
          y
        );

        y += 5;
      }

      if (
        contacts.length
      ) {
        setFont(
          7.8,
          "normal",
          theme.muted
        );

        wrapped(
          contacts.join(
            "  •  "
          ),
          CONTENT_W
        ).forEach(
          (lineText) => {
            doc.text(
              lineText,
              M,
              y
            );

            y += 3.5;
          }
        );
      }

      drawFill(
        M,
        y + 1,
        CONTENT_W,
        2.4,
        theme.pale,
        1
      );

      y += 7;

      return;
    }

    if (
      theme.header ===
      "technical"
    ) {
      drawFill(
        M,
        y - 1,
        2.8,
        22,
        theme.accent
      );

      setFont(
        20.5,
        "bold",
        theme.ink
      );

      doc.text(
        name,
        M + 7,
        y + 7
      );

      if (role) {
        setFont(
          8.8,
          "bold",
          theme.accent
        );

        doc.text(
          role.toUpperCase(),
          M + 7,
          y + 13
        );
      }

      y += 25;

      if (
        contacts.length
      ) {
        setFont(
          7.8,
          "normal",
          theme.muted
        );

        wrapped(
          contacts.join(
            "  |  "
          ),
          CONTENT_W
        ).forEach(
          (lineText) => {
            doc.text(
              lineText,
              M,
              y
            );

            y += 3.5;
          }
        );
      }

      y += 2;

      return;
    }

    if (
      theme.header ===
      "tech"
    ) {
      drawFill(
        M,
        y - 4,
        CONTENT_W,
        24,
        theme.pale,
        2
      );

      drawFill(
        M,
        y - 4,
        3,
        24,
        theme.accent
      );

      setFont(
        20,
        "bold",
        theme.ink
      );

      doc.text(
        name,
        M + 8,
        y + 5
      );

      if (role) {
        setFont(
          8.6,
          "bold",
          theme.accent
        );

        doc.text(
          role.toUpperCase(),
          M + 8,
          y + 11
        );
      }

      if (
        contacts.length
      ) {
        setFont(
          7.5,
          "normal",
          theme.muted
        );

        const contactText =
          contacts.join(
            "  ·  "
          );

        const contactLines =
          wrapped(
            contactText,
            CONTENT_W - 12
          );

        contactLines.forEach(
          (lineText, index) => {
            doc.text(
              lineText,
              M + 8,
              y +
                17 +
                index * 3.2
            );
          }
        );
      }

      y += 27;

      return;
    }

    if (
      theme.header ===
      "creative"
    ) {
      drawFill(
        M,
        y - 5,
        CONTENT_W,
        28,
        theme.pale,
        3
      );

      drawFill(
        PAGE_W - M - 13,
        y + 1,
        8,
        8,
        theme.accent,
        4
      );

      setFont(
        21,
        "bold",
        theme.ink
      );

      doc.text(
        name,
        M + 7,
        y + 6
      );

      if (role) {
        setFont(
          8.8,
          "bold",
          theme.accent
        );

        doc.text(
          role,
          M + 7,
          y + 13
        );
      }

      if (
        contacts.length
      ) {
        setFont(
          7.5,
          "normal",
          theme.muted
        );

        wrapped(
          contacts.join(
            "  •  "
          ),
          CONTENT_W - 28
        ).forEach(
          (lineText, index) => {
            doc.text(
              lineText,
              M + 7,
              y +
                19 +
                index * 3.2
            );
          }
        );
      }

      y += 30;

      return;
    }

    // Minimal / Modern
    setFont(
      theme.header ===
        "modern"
        ? 22
        : 21,
      "bold",
      theme.ink
    );

    doc.text(
      name,
      M,
      y
    );

    y += 7;

    if (role) {
      setFont(
        9.1,
        "bold",
        theme.accent
      );

      doc.text(
        role,
        M,
        y
      );

      y += 5;
    }

    if (
      contacts.length
    ) {
      setFont(
        7.8,
        "normal",
        theme.muted
      );

      wrapped(
        contacts.join(
          "  |  "
        ),
        CONTENT_W
      ).forEach(
        (lineText) => {
          doc.text(
            lineText,
            M,
            y
          );

          y += 3.5;
        }
      );
    }

    drawLine(
      M,
      y + 1,
      PAGE_W - M,
      y + 1,
      theme.header ===
        "modern"
        ? 0.9
        : 0.45,
      theme.accent
    );

    y += 7;
  };

  const renderSummary = () => {
    if (
      !has(data.summary)
    ) {
      return;
    }

    sectionTitle(
      "Professional Summary",
      1
    );

    textBlock(
      data.summary,
      M,
      CONTENT_W,
      theme.font ===
        "times"
        ? 9.5
        : 9,
      4.05
    );

    y += 1;
  };

  const renderExperience = () => {
    const items =
      arr(data.experience)
        .filter(
          (item) =>
            item &&
            (
              has(item.title) ||
              has(item.company) ||
              arr(
                item.bullets
              ).some(Boolean)
            )
        );

    if (!items.length) {
      return;
    }

    sectionTitle(
      "Work Experience",
      2
    );

    items.forEach(
      (item) => {
        ensure(12);

        const title =
          [
            item.title,
            item.company,
          ]
            .filter(Boolean)
            .join(" — ") ||
          "Role";

        const dates =
          dateRange(item);

        drawTitleRow(
          title,
          dates,
          M,
          PAGE_W - M,
          9.4
        );

        if (
          has(item.location)
        ) {
          textBlock(
            item.location,
            M,
            CONTENT_W,
            7.7,
            3.3,
            "italic",
            theme.muted
          );
        }

        arr(
          item.bullets
        )
          .filter(Boolean)
          .forEach(
            (bullet) => {
              const size = 8.8;
              const leading = 3.75;

              setFont(
                size,
                "normal",
                theme.ink
              );

              const lines =
                wrapped(
                  bullet,
                  CONTENT_W - 6
                );

              lines.forEach(
                (
                  lineText,
                  index
                ) => {
                  ensure(
                    leading
                  );

                  if (
                    index === 0
                  ) {
                    doc.text(
                      "•",
                      M + 0.5,
                      y
                    );
                  }

                  doc.text(
                    lineText,
                    M + 5,
                    y
                  );

                  y +=
                    leading;
                }
              );
            }
          );

        y += 1.2;
      }
    );
  };

  const renderEducation = () => {
    const items =
      arr(data.education)
        .filter(
          (item) =>
            item &&
            (
              has(item.degree) ||
              has(item.school)
            )
        );

    if (!items.length) {
      return;
    }

    sectionTitle(
      "Education",
      3
    );

    items.forEach(
      (item) => {
        ensure(9);

        const title =
          [
            item.degree,
            item.school,
          ]
            .filter(Boolean)
            .join(" — ") ||
          "Education";

        drawTitleRow(
          title,
          dateRange(item),
          M,
          PAGE_W - M,
          9.2
        );

        const meta =
          [
            item.location,
            has(item.gpa)
              ? `GPA ${clean(
                  item.gpa
                )}`
              : "",
          ]
            .filter(Boolean)
            .join("  ·  ");

        if (meta) {
          textBlock(
            meta,
            M,
            CONTENT_W,
            7.7,
            3.3,
            "italic",
            theme.muted
          );
        }

        y += 0.8;
      }
    );
  };

  const renderSkills = () => {
    const items =
      arr(data.skills)
        .map(clean)
        .filter(Boolean);

    if (!items.length) {
      return;
    }

    sectionTitle(
      "Skills",
      4
    );

    if (
      theme.skill ===
      "text"
    ) {
      textBlock(
        items.join(
          "  ·  "
        ),
        M,
        CONTENT_W,
        8.7,
        3.8
      );

      y += 1;

      return;
    }

    if (
      theme.skill ===
        "technical" ||
      theme.skill ===
        "code"
    ) {
      setFont(
        8.5,
        "normal",
        theme.ink
      );

      let currentLine =
        "";

      items.forEach(
        (
          skill,
          index
        ) => {
          const candidate =
            currentLine
              ? `${currentLine}  ·  ${skill}`
              : skill;

          if (
            currentLine &&
            doc.getTextWidth(
              candidate
            ) > CONTENT_W
          ) {
            ensure(4);

            doc.text(
              currentLine,
              M,
              y
            );

            y += 3.8;

            currentLine =
              skill;
          } else {
            currentLine =
              candidate;
          }

          if (
            index ===
              items.length -
                1 &&
            currentLine
          ) {
            ensure(4);

            doc.text(
              currentLine,
              M,
              y
            );

            y += 3.8;
          }
        }
      );

      y += 1;

      return;
    }

    /*
     * Pill skills.
     *
     * Every pill is measured before drawing.
     * This prevents pills from crossing the page edge.
     */
    let x = M;
    let rowY = y;

    items.forEach(
      (skill) => {
        setFont(
          7.5,
          "normal",
          theme.accent
        );

        const pillWidth =
          Math.min(
            CONTENT_W,
            doc.getTextWidth(
              skill
            ) + 6
          );

        if (
          x !== M &&
          x +
            pillWidth >
            PAGE_W - M
        ) {
          y =
            rowY + 7;

          x = M;

          rowY = y;
        }

        ensure(6);

        drawFill(
          x,
          rowY - 3.3,
          pillWidth,
          5.5,
          theme.pale,
          1.8
        );

        doc.text(
          skill,
          x + 3,
          rowY
        );

        x +=
          pillWidth + 2.2;
      }
    );

    y =
      rowY + 7;
  };

  const renderCertifications =
    () => {
      const items =
        arr(
          data.certifications
        ).filter(
          (item) =>
            item &&
            (
              has(item.name) ||
              has(item.issuer) ||
              has(item.year)
            )
        );

      if (!items.length) {
        return;
      }

      sectionTitle(
        "Certifications",
        5
      );

      items.forEach(
        (item) => {
          const value =
            [
              item.name,
              item.issuer,
              item.year,
            ]
              .filter(Boolean)
              .join(" — ");

          if (!value) {
            return;
          }

          setFont(
            8.6,
            "normal",
            theme.ink
          );

          const lines =
            wrapped(
              value,
              CONTENT_W - 5
            );

          lines.forEach(
            (
              lineText,
              index
            ) => {
              ensure(3.8);

              if (
                index === 0
              ) {
                doc.text(
                  "•",
                  M + 0.5,
                  y
                );
              }

              doc.text(
                lineText,
                M + 5,
                y
              );

              y += 3.8;
            }
          );
        }
      );

      y += 1;
    };

  const renderProjects = () => {
    const items =
      arr(data.projects)
        .filter(
          (item) =>
            item &&
            (
              has(item.name) ||
              has(item.description) ||
              has(item.tech) ||
              has(item.link)
            )
        );

    if (!items.length) {
      return;
    }

    sectionTitle(
      "Projects",
      6
    );

    items.forEach(
      (item) => {
        ensure(8);

        setFont(
          9.1,
          "bold",
          theme.ink
        );

        doc.text(
          clean(item.name) ||
            "Project",
          M,
          y
        );

        y += 4;

        if (
          has(item.description)
        ) {
          textBlock(
            item.description,
            M,
            CONTENT_W,
            8.6,
            3.75
          );
        }

        const meta =
          [
            item.tech,
            item.link,
          ]
            .filter(Boolean)
            .join("  |  ");

        if (meta) {
          textBlock(
            meta,
            M,
            CONTENT_W,
            7.5,
            3.3,
            "normal",
            theme.muted
          );
        }

        y += 1;
      }
    );
  };

  const renderLanguages = () => {
    const items =
      arr(data.languages)
        .filter(
          (item) =>
            item &&
            has(item.name)
        );

    if (!items.length) {
      return;
    }

    sectionTitle(
      "Languages",
      7
    );

    const value =
      items
        .map(
          (item) =>
            has(item.level)
              ? `${clean(
                  item.name
                )} (${clean(
                  item.level
                )})`
              : clean(
                  item.name
                )
        )
        .join("  ·  ");

    textBlock(
      value,
      M,
      CONTENT_W,
      8.5,
      3.8
    );
  };

  drawHeader();

  const renderers = {
    summary:
      renderSummary,

    experience:
      renderExperience,

    education:
      renderEducation,

    skills:
      renderSkills,

    certifications:
      renderCertifications,

    projects:
      renderProjects,

    languages:
      renderLanguages,
  };

  theme.order.forEach(
    (section) => {
      const renderer =
        renderers[section];

      if (renderer) {
        renderer();
      }
    }
  );

  const totalPages =
    doc.getNumberOfPages();

  /*
   * Add page numbers only when the resume
   * actually needs more than one page.
   */
  if (
    totalPages > 1
  ) {
    for (
      let page = 1;
      page <= totalPages;
      page += 1
    ) {
      doc.setPage(page);

      setFont(
        6.7,
        "normal",
        [130, 130, 135]
      );

      doc.text(
        `${page} / ${totalPages}`,
        PAGE_W - M,
        PAGE_H - 7,
        {
          align: "right",
        }
      );
    }
  }

  doc.save(
    `${safeFileName(
      data.full_name ||
        resume?.name
    )}.pdf`
  );
}

export default downloadResumePDF;
